/**
 * Public Sentiment prompt v19 — the DETERMINISTIC EVIDENCE TABLES block.
 *
 * The redesigned report prints the psr-meta-v2 counts as tables and charts
 * beside the model's prose. This renders the same projection as plain text
 * for the Public Sentiment narrative prompt only, so the prose agrees with
 * the tables: per-technology stance from the LOCAL split (not the SED
 * perTech means, which mix scopes and zero-weight unscored rows), a
 * low-volume caveat for thin months, and an appendix that points to the
 * ledger instead of re-listing records.
 *
 * Emitted only for an ASSESSED report with a valid v2 projection. The other
 * outcomes never call the model, and a report whose projection fell back to
 * {} gets no block (its prompt is then byte-identical to v18's body).
 */
import type { PublicSentimentBtmTech, PublicSentimentScope } from '../../types/siteEvidence';
import type { SentimentAssessment } from './publicSentimentScope';
import { SENTIMENT_REASON_LABELS } from './publicSentimentReasonCodes';
import { readPublicSentimentMetaV2 } from './publicSentimentMetaV2Contract';

export const PSR_TECH_LABELS: Readonly<Record<PublicSentimentBtmTech, string>> = Object.freeze({
  'general-datacenter': 'Data center (general)',
  'natural-gas': 'Natural gas',
  'solar-bess': 'Solar + storage',
  'wind-bess': 'Wind + storage',
  'nuclear-smr': 'Nuclear / SMR',
});

export const PSR_SCOPE_LABELS: Readonly<Record<PublicSentimentScope, string>> = Object.freeze({
  site: 'Local (site / county)',
  state: 'Statewide context',
  country: 'National context',
});

/** A month resting on this many statements or fewer is low volume. */
export const PSR_LOW_VOLUME_MONTH_MAX = 3;

const techLabel = (tech: string) => PSR_TECH_LABELS[tech as PublicSentimentBtmTech] ?? tech;

export function buildPublicSentimentEvidenceTablesBlock(meta: unknown, assessment?: SentimentAssessment | null): string {
  const v2 = readPublicSentimentMetaV2(meta);
  if (!v2 || v2.reportState !== 'assessed' || !v2.scopeCounts?.length || !v2.stanceByTech) return '';
  const local = v2.scopeCounts.find(row => row.scope === 'site');
  if (!local) return '';
  const stanced = (row: { supportive?: number; neutralStated?: number; opposed?: number }) =>
    (row.supportive ?? 0) + (row.neutralStated ?? 0) + (row.opposed ?? 0);
  const lines: string[] = [
    '═══ DETERMINISTIC EVIDENCE TABLES (computed from the SED rows above; the report prints them as tables and charts beside your prose) ═══',
    'Local stance = dated, explicit, attributable statements at site scope — exactly the statements the support index counts. Unscored records carry no stance and are never neutral. Statewide and national records are context, never local stance.',
    'Table 1 — Records by scope (total · with a stance: supportive / neutral statement / opposed · unscored):',
    ...v2.scopeCounts.map(row => `- ${PSR_SCOPE_LABELS[row.scope]}: ${row.total} · ${stanced(row)}: ${row.supportive ?? 0} / ${row.neutralStated ?? 0} / ${row.opposed ?? 0} · ${row.unscored}`),
    'Table 2 — Local stance by selected technology (supportive / neutral statement / opposed · unscored of local records):',
    ...v2.stanceByTech.map(row => row.supportive + row.neutralStated + row.opposed === 0
      ? `- ${techLabel(row.btmTech)}: no assessed local statements · ${row.unscored} unscored of ${row.total}`
      : `- ${techLabel(row.btmTech)}: ${row.supportive} / ${row.neutralStated} / ${row.opposed} · ${row.unscored} unscored of ${row.total}`),
  ];
  if (v2.unscoredByReason?.length) {
    lines.push('Table 3 — Unscored records by reason (local / context):',
      ...v2.unscoredByReason.map(row => `- ${SENTIMENT_REASON_LABELS[row.reasonCode] ?? row.reasonCode}: ${row.local} / ${row.context}`));
  }
  const monthly = (assessment?.monthly ?? []).filter(row => typeof row.month === 'string' && Number.isFinite(row.count) && Number.isFinite(row.supportIndex));
  if (monthly.length) {
    lines.push(`Table 4 — Monthly support index, dated local statements only (low volume = ${PSR_LOW_VOLUME_MONTH_MAX} or fewer statements):`,
      ...monthly.map(row => `- ${row.month}: n=${row.count}, index ${row.supportIndex}${row.count <= PSR_LOW_VOLUME_MONTH_MAX ? ' (low volume)' : ''}`));
  }
  const prior = v2.priorScan;
  if (prior?.sameQuery && prior.supportIndex !== undefined && prior.supportIndexDelta !== undefined && prior.localObservationCount !== undefined) {
    const delta = prior.supportIndexDelta > 0 ? `+${prior.supportIndexDelta}` : String(prior.supportIndexDelta);
    lines.push(`Table 5 — Since the prior scan of ${prior.fetchedAt} (same query): index ${prior.supportIndex} → ${prior.supportIndex + prior.supportIndexDelta} (${delta} points), resting on ${prior.localObservationCount} → ${local.supportive! + local.neutralStated! + local.opposed!} local statements${prior.addedObservationCount !== undefined ? `; ${prior.addedObservationCount} records are new since that scan (all scopes)` : ''}. This compares evidence inventories, not population opinion.`);
  }
  if (v2.ledgerTotal) {
    lines.push(`Evidence ledger: ${v2.ledgerTotal.local + v2.ledgerTotal.context} records (${v2.ledgerTotal.local} local, ${v2.ledgerTotal.context} context) are printed in the appendix with date, source, stance or reason, and excerpt.`);
  }
  lines.push(
    'RULES FOR THESE TABLES:',
    '- Per-BTM Sentiment: describe each selected technology from Table 2 only. SED publicSentiment.perTech and perScope means mix scopes and include unscored rows at zero weight; never cite them as stance.',
    '- Trend Analysis: a low-volume month moves on a single statement. Say so, and do not describe a trend from low-volume months alone.',
    '- Appendix: state the method and its limits and refer to the evidence ledger; do not re-list individual records.',
    '- Every count you state must match these tables. Do not compute another split, and do not call unscored records neutral.',
  );
  return lines.join('\n');
}
